import React from 'react';
import { Card, Radio, Row, Col, Typography, Space } from 'antd';
import { FieldConfig } from '../../types/fieldTypes';

const { Title, Text } = Typography;

interface CardsFieldProps {
  field: FieldConfig;
  value: any;
  onChange: (value: any) => void;
  mode?: 'edit' | 'view';
  formData?: Record<string, any>;
}

export const CardsField: React.FC<CardsFieldProps> = ({
  field,
  value,
  onChange,
  mode = 'edit',
  formData,
}) => {
  const handleRadioChange = (e: any) => {
    const optionValue = e.target.value;

    // Get complete package information
    const packageInfo = {
      value: optionValue,
      name: getPackageName(optionValue),
      description: getPackageDescription(optionValue),
      price: getPackagePriceValue(optionValue),
      features: getPackageFeatures(optionValue),
      color: getPackageColor(optionValue),
      icon: getPackageIcon(optionValue),
    };

    // Create a proper event object that DynamicForm expects
    const customEvent = {
      target: {
        name: field.name || 'selectedPackage',
        value: packageInfo,
      },
    };

    onChange(customEvent);
  };

  // Get current selected value for radio
  const getCurrentValue = () => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.value) return value.value;
    return undefined;
  };

  if (mode === 'view') {
    const selectedPackage = value?.name || value;
    const packageDescription = value?.description || '';
    return (
      <Card style={{ width: '100%' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Title level={4}>{selectedPackage || 'No package selected'}</Title>
          {packageDescription && (
            <Text type="secondary">{packageDescription}</Text>
          )}
          {value?.price && (
            <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
              {value.price} ALL/month
            </Text>
          )}
        </Space>
      </Card>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        {field.label}
      </Title>

      <Radio.Group
        key={getCurrentValue() || 'none'}
        value={getCurrentValue()}
        onChange={handleRadioChange}
        style={{ width: '100%' }}
        defaultValue={field.options?.[0]?.value}
      >
        <Row gutter={[24, 24]} justify="center">
          {field.options?.map((option, index) => (
            <Col key={option.value} xs={24} sm={12} md={8} lg={6} xl={6}>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <Radio
                  value={option.value}
                  style={{ fontSize: '16px', fontWeight: '500' }}
                  checked={getCurrentValue() === option.value}
                >
                  {getPackageName(option.value)} Package
                </Radio>
              </div>

              <Card
                hoverable
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '400px',
                  border:
                    getCurrentValue() === option.value
                      ? '3px solid #1890ff'
                      : '2px solid #e8e8e8',
                  borderRadius: '12px',
                  boxShadow:
                    getCurrentValue() === option.value
                      ? '0 6px 16px rgba(24, 144, 255, 0.2)'
                      : '0 4px 12px rgba(0, 0, 0, 0.08)',
                  cursor: 'pointer',
                  background:
                    getCurrentValue() === option.value
                      ? 'linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)'
                      : '#ffffff',
                }}
                bodyStyle={{
                  padding: '24px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
                onClick={() => {
                  // Trigger radio button selection when card is clicked
                  const radioEvent = {
                    target: { value: option.value },
                  };
                  handleRadioChange(radioEvent);
                }}
              >
                <div
                  style={{
                    textAlign: 'center',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Smart Choice Badge for Gold */}
                  {option.value === 'gold' && (
                    <div
                      style={{
                        position: 'absolute',
                        top: -8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#ff6b35',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        zIndex: 10,
                      }}
                    >
                      Smart Choice
                    </div>
                  )}

                  {/* Package Icon */}
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: getPackageColor(option.value),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      fontSize: '32px',
                      fontWeight: 'bold',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    {getPackageIcon(option.value)}
                  </div>

                  {/* Package Name */}
                  <Title
                    level={3}
                    style={{ margin: '0 0 8px 0', color: '#262626' }}
                  >
                    {getPackageName(option.value)}
                  </Title>

                  {/* Package Description */}
                  <Text
                    style={{
                      color: '#8c8c8c',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      marginBottom: '20px',
                      textAlign: 'center',
                    }}
                  >
                    {getPackageDescription(option.value)}
                  </Text>

                  {/* Features */}
                  <div style={{ flex: 1, marginBottom: '20px' }}>
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: '100%' }}
                    >
                      {getPackageFeatures(option.value).map(
                        (feature, featureIndex) => (
                          <div
                            key={featureIndex}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                            }}
                          >
                            <div
                              style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: '#52c41a',
                                marginRight: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <span style={{ color: 'white', fontSize: '8px' }}>
                                ✓
                              </span>
                            </div>
                            <Text
                              style={{ fontSize: '12px', lineHeight: '1.4' }}
                            >
                              {feature}
                            </Text>
                          </div>
                        )
                      )}
                    </Space>
                  </div>

                  {/* Price */}
                  <div
                    style={{
                      marginTop: 'auto',
                      padding: '16px 0',
                      textAlign: 'center',
                      borderTop: '1px solid #f0f0f0',
                    }}
                  >
                    <div style={{ marginBottom: '4px' }}>
                      <Text
                        style={{
                          fontSize: '12px',
                          color: '#8c8c8c',
                          fontWeight: '500',
                        }}
                      >
                        Starting from
                      </Text>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: '2px',
                      }}
                    >
                      <Text
                        style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: '#1890ff',
                          lineHeight: '1.2',
                        }}
                      >
                        {getPackagePriceValue(option.value)} ALL
                      </Text>
                      <Text
                        style={{
                          fontSize: '14px',
                          color: '#8c8c8c',
                          fontWeight: '500',
                        }}
                      >
                        /month
                      </Text>
                    </div>
                    <div style={{ marginTop: '4px' }}>
                      <Text
                        style={{
                          fontSize: '10px',
                          color: '#8c8c8c',
                          fontStyle: 'italic',
                        }}
                      >
                        *Terms apply
                      </Text>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Radio.Group>
    </div>
  );
};

// Helper functions for package data
const getPackageColor = (value: string) => {
  const colors = {
    platinum: '#c0c0c0',
    gold: '#ffd700',
    silver: '#c0c0c0',
    bronze: '#cd7f32',
  };
  return colors[value as keyof typeof colors] || '#1890ff';
};

const getPackageIcon = (value: string) => {
  const icons = {
    platinum: 'P',
    gold: 'G',
    silver: 'S',
    bronze: 'B',
  };
  return icons[value as keyof typeof icons] || '?';
};

const getPackageName = (value: string) => {
  const names = {
    platinum: 'Platinum',
    gold: 'Gold',
    silver: 'Silver',
    bronze: 'Bronze',
  };
  return names[value as keyof typeof names] || value;
};

const getPackageDescription = (value: string) => {
  const descriptions = {
    platinum: 'Maximum exclusive performance for profitable growth.',
    gold: 'Differentiate yourself from the competition and stand out.',
    silver: 'Increase sales opportunities easily and efficiently.',
    bronze: 'Benefit directly from a big reach.',
  };
  return descriptions[value as keyof typeof descriptions] || '';
};

const getPackageFeatures = (value: string) => {
  const features = {
    platinum: [
      'Highest organic visibility',
      'All Demand Tools',
      'Up to 5 x free contingents',
      'Direct Offer',
      'Premium Customer Service',
      'Cross-Location Ads',
      'Social+',
    ],
    gold: [
      'High organic visibility',
      'All Demand Tools',
      'Up to 3 x free contingents',
      'Direct Offer',
      'Premium Customer Service',
    ],
    silver: [
      'Increased organic visibility',
      'All Demand Tools',
      'Free contingents',
      'Direct Offer',
    ],
    bronze: ['Normal organic visibility', 'Individual Demand Tools'],
  };
  return features[value as keyof typeof features] || [];
};

const getPackagePrice = (value: string) => {
  const prices = {
    platinum: 'From ALL 144.99/month*',
    gold: 'From ALL 116.99/month*',
    silver: 'From ALL 94.99/month*',
    bronze: 'From ALL 72.99/month*',
  };
  return prices[value as keyof typeof prices] || '';
};

const getPackagePriceValue = (value: string) => {
  const prices = {
    platinum: '144.99',
    gold: '116.99',
    silver: '94.99',
    bronze: '72.99',
  };
  return prices[value as keyof typeof prices] || '0.00';
};
